import React, { memo, useCallback, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import throttle from 'lodash/throttle';
import useEventListener from 'src/hooks/useEventListener';
import styles from './styles.scss';

const TipBox = ({ show, content, position, align }) => {
  const [visible, setVisible] = useState(false);
  const tipBoxRef = useRef();
  const modifiedPosition = useRef(position);
  const modifiedAlignment = useRef(align);

  /*
    This applies rudimentary logic to detect if the tooltip is extending
    beyond the browser window. If it extends beyond, then change its position
    and alignment.
  */
  useEffect(() => {
    if (show) {
      const { left: tipBoxLeft, right: tipBoxRight } = tipBoxRef.current.getBoundingClientRect();
      const { innerWidth: windowWidth } = window;

      if (position === 'top' || position === 'bottom') {
        if (tipBoxRight > windowWidth) {
          modifiedAlignment.current = 'right';
        } else if (tipBoxLeft < 0) {
          modifiedAlignment.current = 'left';
        }
      } else if (position === 'left') {
        if (tipBoxLeft < 0) {
          modifiedAlignment.current = 'left';
          modifiedPosition.current = 'bottom';
        }
      } else if (position === 'right') {
        if (tipBoxRight > windowWidth) {
          modifiedAlignment.current = 'right';
          modifiedPosition.current = 'bottom';
        }
      }
      setVisible(true);
    } else {
      setVisible(false);
      modifiedPosition.current = position;
      modifiedAlignment.current = align;
    }
  }, [show]);

  const positionStyle = styles[modifiedPosition.current];
  const alignStyle = styles[`align-${modifiedAlignment.current}`];
  const visibilityStyle = visible ? '' : styles.hidden;

  return (
    <div className={cn(styles.scaffoldingBox, positionStyle, alignStyle, visibilityStyle)}>
      <div className={cn(styles.positioningBox, positionStyle, alignStyle)}>
        <div className={cn(styles.hoverScaffoldingBox, positionStyle)}>
          <div ref={tipBoxRef} className={styles.tipBox}>
            <p className="caption">{content}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

TipBox.propTypes = {
  show: PropTypes.bool.isRequired,
  content: PropTypes.string,
  position: PropTypes.string,
  align: PropTypes.string,
};

const Tooltip = ({
  children,
  hoverDuration = 200,
  useHover = true,
  content = '',
  position = 'bottom',
  align = 'center',
  wrapperClass = '',
}) => {
  const [showTip, setShowTip] = useState(false);
  const touchEnabled = useRef(false);
  const hoverEnabled = useRef(false);
  const timeoutId = useRef();
  const containerRef = useRef();

  const onTouchStart = useCallback(() => {
    touchEnabled.current = true;
  }, []);

  const onMouseEnter = useCallback(() => {
    if (!touchEnabled.current && useHover) {
      timeoutId.current = setTimeout(() => setShowTip(true), hoverDuration);
      hoverEnabled.current = true;
    }
    touchEnabled.current = false;
  }, []);

  const onClick = useCallback(() => {
    if (!hoverEnabled.current) {
      setShowTip(!showTip);
    }
  }, [showTip]);

  const onMouseLeave = useCallback(() => {
    clearTimeout(timeoutId.current);
    if (hoverEnabled.current && showTip && useHover) {
      setShowTip(false);
    }
    hoverEnabled.current = false;
  }, [showTip]);

  useEffect(() => {
    return () => {
      clearTimeout(timeoutId.current);
    };
  }, []);

  /*
    Close self on touch-enabled devices if user taps elsewhere.
    This method isn't great for browser performance if there are many
    instances of Tooltip on a page.
  */
  const closeOtherTooltips = useCallback((e) => {
    if (!containerRef.current.contains(e.target)) {
      setShowTip(false);
    }
  }, []);

  useEventListener({ eventName: 'touchstart', handler: closeOtherTooltips });

  /**
    Close the tooltip if the user scrolls
  */
  const handleCloseOnScroll = throttle(() => {
    if (showTip) {
      setShowTip(false);
    }
  }, 500);

  useEventListener({ eventName: 'scroll', handler: handleCloseOnScroll });

  return (
    <div
      onTouchStart={onTouchStart}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      ref={containerRef}
      className={cn('site-v2', styles.wrapper, wrapperClass)}
    >
      {children}
      <TipBox show={showTip} content={content} position={position} align={align} />
    </div>
  );
};

Tooltip.propTypes = {
  children: PropTypes.any,
  hoverDuration: PropTypes.number,
  useHover: PropTypes.bool,
  content: PropTypes.string,
  position: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  align: PropTypes.oneOf(['right', 'left', 'center', 'top', 'bottom']),
  wrapperClass: PropTypes.string,
};

export default memo(Tooltip);

/*
  Tooltip

  Given an element, show a tooltip at a desired position and alignment.

  The design specs call for the tooltip to be positioned bottom and aligned
  center, so there is no need to pass values to these props for most cases.

  The specs also call for showing the tooltip when the given element is hovered
  on desktop. However, this should really mean the tooltip should be shown via
  hover if the user's device can support it. This is why both touch and mouse
  events are being used.

  Props:
  - content - String - The content of the tooltip
  - position - String - The position of the tooltip relative to the element.
  - align - String - Align the tooltip relative to the element.
  - hoverDuration - Number - Time in ms to wait before showing the tooltip when hovered.
  - useHover - Boolean - Toggle whether to use hover or touch/click to show the tooltip.
  - wrapperClass - String - Class to be applied to the wrapper div.

  ------

  Usage:

  import Tooltip from 'src/components/HTKit/Elements/Tooltip';

  ...

  return (
    <Tooltip content="Here's the tip: Drink lots of water, and call your mom.">
      <p>I have a tip!</p>
    </Tooltip>
  );
*/
